import { computed, ref, type Ref } from 'vue';

type ApplyEstimationData = (data: any, options?: { showWarnings?: boolean }) => Promise<void> | void;

export const usePartnerEstimates = (options?: { onClear?: () => void }) => {
  const defaultEstimateData: Ref<any | null> = ref(null);
  const allPartnerEstimates: Ref<any[]> = ref([]);
  const selectedPartnerEstimateIndex = ref(0);

  const partnerEstimateTabs = computed(() => [
    { key: 'no-partner', label: 'No Partner' },
    ...allPartnerEstimates.value.map((partnerEstimate, index) => ({
      key: `partner-${partnerEstimate.partnerId ?? index}`,
      label: `Partner #${index + 1}`,
    })),
  ]);

  const selectedPartnerEstimate = computed(() => {
    if (selectedPartnerEstimateIndex.value <= 0) {
      return null;
    }

    return allPartnerEstimates.value[selectedPartnerEstimateIndex.value - 1] || null;
  });

  const selectedPartnerId = computed(() => selectedPartnerEstimate.value?.partnerId ?? null);

  const setPartnerEstimatesFromResponse = (data: any) => {
    const partnerEstimates = data.allPartnerEstimates;
    defaultEstimateData.value = data;
    allPartnerEstimates.value = Array.isArray(partnerEstimates) ? partnerEstimates : [];

    if (allPartnerEstimates.value.length > 0) {
      selectedPartnerEstimateIndex.value = 1;
      return allPartnerEstimates.value[0];
    }

    selectedPartnerEstimateIndex.value = 0;
    return data;
  };

  const resetPartnerEstimates = () => {
    defaultEstimateData.value = null;
    allPartnerEstimates.value = [];
    selectedPartnerEstimateIndex.value = 0;
    options?.onClear?.();
  };

  const selectPartnerEstimateTab = async (index: number, applyEstimationData: ApplyEstimationData) => {
    if (index === selectedPartnerEstimateIndex.value) {
      return;
    }

    if (index === 0) {
      if (!defaultEstimateData.value) {
        return;
      }

      selectedPartnerEstimateIndex.value = 0;
      options?.onClear?.();
      await applyEstimationData(defaultEstimateData.value, { showWarnings: false });
      return;
    }

    const partnerEstimate = allPartnerEstimates.value[index - 1];
    if (!partnerEstimate) {
      return;
    }

    selectedPartnerEstimateIndex.value = index;
    options?.onClear?.();
    await applyEstimationData(partnerEstimate, { showWarnings: false });
  };

  const toNumericPartnerId = (value?: number | string | null): number | undefined => {
    if (value == null || value === '') {
      return undefined;
    }

    const numericPartnerId = Number(value);
    return Number.isFinite(numericPartnerId) ? numericPartnerId : undefined;
  };

  const getOrderingPartnerId = (fallbackPartnerId?: number | string | null) =>
    toNumericPartnerId(selectedPartnerId.value) ??
    (allPartnerEstimates.value.length > 0 ? undefined : toNumericPartnerId(fallbackPartnerId));

  return {
    allPartnerEstimates,
    selectedPartnerEstimateIndex,
    partnerEstimateTabs,
    selectedPartnerEstimate,
    selectedPartnerId,
    setPartnerEstimatesFromResponse,
    resetPartnerEstimates,
    selectPartnerEstimateTab,
    getOrderingPartnerId,
  };
};
